import { useNavigate, useSearch } from "@tanstack/react-router";

import { DeltaCell } from "@/components/cinch/DeltaCell";
import { SectionDivider } from "@/components/cinch/SectionDivider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

import { useKpiHistory, useSummary } from "../api";
import { BigKpiTile, type KpiBreakdownItem } from "../components/BigKpiTile";
import { ChurnContractionTable } from "../components/ChurnContractionTable";
import {
  ClosedTileDrawer,
  ForecastTileDrawer,
} from "../components/ClosedTileDrawer";
import { CroForecastFilterBar } from "../components/FilterBar";
import { HubspotForecastCard } from "../components/HubspotForecastCard";
import { LlmCommentaryCard } from "../components/LlmCommentaryCard";
import { LockedBanner } from "../components/LockedBanner";
import { NewRevenueTable } from "../components/NewRevenueTable";
import { NotesArea } from "../components/NotesArea";
import { PipelineBanner } from "../components/PipelineBanner";
import { PipelineTileDrawer } from "../components/PipelineTileDrawer";
import { PipelineBySourceCard } from "../components/PipelineBySourceCard";
import { CroForecastSubNav } from "../components/SubNav";
import { TotalRowCard } from "../components/TotalRowCard";
import { fmtCents, fmtMonthName, fmtPct, fmtQuarter } from "../format";
import {
  resolveSearch,
  type CroForecastSearch,
  type CroTile,
} from "../search";

export function CroForecastSummaryPage() {
  const search = useSearch({ from: "/cro-forecast/summary" }) as CroForecastSearch;
  const navigate = useNavigate();
  const { year, week } = resolveSearch(search);
  const summary = useSummary(year, week);
  const history = useKpiHistory(12);

  return (
    <div className="flex flex-col">
      <CroForecastFilterBar search={search} />
      <CroForecastSubNav search={search} />

      <div className="flex flex-col gap-6 p-4 md:p-6">
        <LockedBanner
          status={summary.data?.lock_status}
          isoYear={year}
          isoWeek={week}
        />
        {summary.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {summary.error && <ErrorPanel error={summary.error} />}
        {summary.data && (
          <SummaryBody
            data={summary.data}
            isoYear={year}
            isoWeek={week}
            history={history.data?.series}
            onOpenTile={(tile) =>
              navigate({
                to: "/cro-forecast/summary",
                search: (prev: CroForecastSearch) => ({
                  ...prev,
                  tile,
                  stage: undefined,
                }),
              })
            }
            onOpenPipeline={(which, stage) =>
              navigate({
                to: "/cro-forecast/summary",
                search: (prev: CroForecastSearch) => ({
                  ...prev,
                  tile: `pipeline_${which}` as CroTile,
                  stage,
                }),
              })
            }
          />
        )}
      </div>

      {summary.data && (
        <TileDrawers
          data={summary.data}
          tile={search.tile}
          stage={search.stage}
          expanded={!!search.expanded}
          go={(next) =>
            navigate({
              to: "/cro-forecast/summary",
              search: (prev: CroForecastSearch) => ({ ...prev, ...next }),
            })
          }
        />
      )}
    </div>
  );
}

/**
 * Mounts the drill-down drawer matching `?tile=`. One component so all four
 * tiles share the same open/expand/close plumbing against the URL.
 */
function TileDrawers({
  data,
  tile,
  stage,
  expanded,
  go,
}: {
  data: import("../api").SummaryPayload;
  tile: CroTile | undefined;
  stage: string | undefined;
  expanded: boolean;
  go: (next: Partial<CroForecastSearch>) => void;
}) {
  const monthLabel = `${fmtMonthName(data.fiscal_month)} ${data.quarter_year}`;
  const quarterLabel = fmtQuarter(data.quarter_year, data.quarter);

  // Closing from expanded returns to the quick view first, matching the
  // NewBusinessDrawer behaviour on the KPI page.
  const close = () =>
    expanded
      ? go({ expanded: undefined })
      : go({ tile: undefined, stage: undefined, expanded: undefined });
  const shared = {
    expanded,
    onExpandedChange: (v: boolean) => go({ expanded: v ? true : undefined }),
    onOpenChange: (v: boolean) => {
      if (!v && !expanded) go({ tile: undefined, stage: undefined, expanded: undefined });
    },
    onClose: close,
  };

  if (tile === "closed_month" || tile === "closed_quarter") {
    const isMonth = tile === "closed_month";
    return (
      <ClosedTileDrawer
        {...shared}
        open
        periodLabel={isMonth ? monthLabel : quarterLabel}
        rollup={isMonth ? data.closed_rollup_month : data.closed_rollup_quarter}
        contracts={
          isMonth ? data.closed_contracts_month : data.closed_contracts_quarter
        }
      />
    );
  }
  if (tile === "forecast_month" || tile === "forecast_quarter") {
    const isMonth = tile === "forecast_month";
    const rows = isMonth
      ? data.forecast_detail_month
      : data.forecast_detail_quarter;
    const segs = isMonth
      ? data.forecast_by_segment_month
      : data.forecast_by_segment_quarter;
    return (
      <ForecastTileDrawer
        {...shared}
        open
        periodLabel={isMonth ? monthLabel : quarterLabel}
        totalCents={sumSegment(segs, "forecast_cents")}
        detail={rows ?? []}
      />
    );
  }
  if (
    tile === "pipeline_sales" ||
    tile === "pipeline_partner" ||
    tile === "pipeline_cs"
  ) {
    const banner =
      tile === "pipeline_sales"
        ? data.banner
        : tile === "pipeline_partner"
        ? data.partner_banner
        : data.cs_banner;
    const heading =
      tile === "pipeline_sales"
        ? banner?.pipeline_name ?? "Sales Pipeline"
        : tile === "pipeline_partner"
        ? "Partner pipeline"
        : "Customer Success pipeline";
    return (
      <PipelineTileDrawer
        {...shared}
        open
        title={heading}
        banner={banner}
        stage={stage}
        onStageChange={(next) => go({ stage: next })}
      />
    );
  }
  return null;
}

function ErrorPanel({ error }: { error: unknown }) {
  const err = error as
    | (Error & { status?: number; payload?: { detail?: unknown } })
    | undefined;
  const detail =
    err?.payload?.detail != null
      ? typeof err.payload.detail === "string"
        ? err.payload.detail
        : JSON.stringify(err.payload.detail)
      : undefined;
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
      <p className="font-semibold">
        Summary failed to load{err?.status ? ` (${err.status})` : ""}.
      </p>
      <p className="mt-1 break-words font-mono text-xs">
        {detail ?? err?.message ?? "no data"}
      </p>
    </div>
  );
}

function SummaryBody({
  data,
  isoYear,
  isoWeek,
  history,
  onOpenTile,
  onOpenPipeline,
}: {
  data: import("../api").SummaryPayload;
  isoYear: number;
  isoWeek: number;
  history: Record<string, { iso_year: number; iso_week: number; value_cents: number }[]> | undefined;
  onOpenTile: (tile: CroTile) => void;
  onOpenPipeline: (which: "sales" | "partner" | "cs", stage?: string) => void;
}) {
  const month = data.fiscal_month;
  const quarter = data.quarter;
  const monthLabel = `${fmtMonthName(month)} ${data.quarter_year}`;
  const quarterLabel = fmtQuarter(data.quarter_year, quarter);
  const priorLabel = data.prior_iso_week ? `Wk ${data.prior_iso_week}` : "Prior";

  // MTD / QTD aggregates. Closed comes from the BE roll-up rather than being
  // re-summed here, so the tile, the New Revenue table, and the Forecast
  // Meeting tab all read the same number from one Snowflake read.
  const mtdRollup = data.closed_rollup_month;
  const qtdRollup = data.closed_rollup_quarter;
  const mtdClosed = mtdRollup?.total_cents ??
    sumSegment(data.closed_by_segment_month, "closed_cents");
  const mtdForecast = sumSegment(data.forecast_by_segment_month, "forecast_cents");
  const mtdUpside = sumSegment(data.upside_by_segment_month, "upside_cents");
  const qtdClosed = qtdRollup?.total_cents ??
    sumSegment(data.closed_by_segment_quarter, "closed_cents");
  const qtdForecast = sumSegment(data.forecast_by_segment_quarter, "forecast_cents");
  const qtdUpside = sumSegment(data.upside_by_segment_quarter, "upside_cents");

  const planMonth = data.plan_month_cents;
  const planQuarter = data.plan_quarter_cents;

  // Pull cents arrays out of the history payload (most recent N weeks).
  const histArr = (key: string): number[] =>
    (history?.[key] ?? []).map((p) => p.value_cents);

  // Week-over-week lookup. The BE already applied the reset rules, so a null
  // here means "not comparable" and the chip renders nothing.
  const wow = (key: string): number | null => data.wow?.[key] ?? null;

  return (
    <>
      {/* Pipeline banner — same as before */}
      <PipelineBanner
        data={data.banner}
        partner={data.partner_banner}
        cs={data.cs_banner}
        wow={wow}
        onOpenTile={onOpenPipeline}
      />

      {/* Six BIG MTD tiles with sparklines */}
      <SectionDivider
        label={monthLabel}
        description="Month-to-date"
        tone="accent"
      />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <BigKpiTile
          label={`Closed`}
          value={fmtCents(mtdClosed)}
          sub={
            <WowSub
              label={`Plan ${fmtCents(planMonth)}`}
              delta={wow("mtd.closed")}
              priorLabel={priorLabel}
            />
          }
          breakdown={rollupBreakdown(mtdRollup)}
          history={histArr("mtd_closed")}
          baseline={planMonth || undefined}
          onOpen={() => onOpenTile("closed_month")}
        />
        <BigKpiTile
          label={`Forecast`}
          value={fmtCents(mtdForecast)}
          sub={
            <WowSub
              delta={wow("mtd.forecast")}
              priorLabel={priorLabel}
            />
          }
          tone="positive"
          history={histArr("mtd_forecast")}
          onOpen={() => onOpenTile("forecast_month")}
        />
        <BigKpiTile
          label={`Upside`}
          value={fmtCents(mtdUpside)}
          sub={<WowSub delta={wow("mtd.upside")} priorLabel={priorLabel} />}
          tone="muted"
          history={histArr("mtd_upside")}
        />
      </div>

      {/* Plan vs Forecast vs Actual — month row */}
      <PlanCard
        label={monthLabel}
        plan={planMonth}
        forecast={mtdForecast}
        actual={mtdClosed}
      />

      {/* Live HubSpot Forecasting view — month */}
      <HubspotForecastCard
        fiscalYear={data.quarter_year}
        periodType="month"
        periodValue={month}
        periodLabel={monthLabel}
      />

      {/* New Revenue + Churn — MTD */}
      <NewRevenueTable
        title={`New Revenue — ${monthLabel}`}
        priorLabel={priorLabel}
        closed={data.closed_by_segment_month}
        forecast={data.forecast_by_segment_month}
        upside={data.upside_by_segment_month}
      />
      <NotesArea
        isoYear={isoYear}
        isoWeek={isoWeek}
        scope="new_revenue"
        initialBody={data.notes?.["new_revenue"] ?? ""}
        label={`Stan's notes — ${monthLabel} new revenue`}
        placeholder="Notes on monthly new revenue (what's driving the number, risks, next steps)…"
      />

      <ChurnContractionTable
        title={`Churn & Contraction — ${monthLabel}`}
        priorLabel={priorLabel}
        closedCents={data.churn_month_cents}
        forecastCents={data.churn_forecast_month_cents}
      />
      <NotesArea
        isoYear={isoYear}
        isoWeek={isoWeek}
        scope="churn"
        initialBody={data.notes?.["churn"] ?? ""}
        label={`Stan's notes — ${monthLabel} churn & contraction`}
        placeholder="Notes on monthly churn / contraction (who's at risk, what's the play)…"
      />

      <TotalRowCard
        label={`Total — ${monthLabel}`}
        priorLabel={priorLabel}
        newRevenueClosed={mtdClosed}
        newRevenueForecast={mtdForecast}
        newRevenueUpside={mtdUpside}
        churnClosed={data.churn_month_cents}
        churnForecast={data.churn_forecast_month_cents}
      />
      <NotesArea
        isoYear={isoYear}
        isoWeek={isoWeek}
        scope="overall"
        initialBody={data.notes?.["overall"] ?? ""}
        label={`Stan's notes — ${monthLabel} net (Stan's overall commentary)`}
        placeholder="Big-picture notes on the month's net (combined new + churn)…"
      />

      {/* Clear divider between Month and Quarter — and now Quarter block */}
      <SectionDivider
        label={quarterLabel}
        description="Quarter-to-date"
        tone="accent"
      />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <BigKpiTile
          label={`Closed`}
          value={fmtCents(qtdClosed)}
          sub={
            <WowSub
              label={`Plan ${fmtCents(planQuarter)}`}
              delta={wow("qtd.closed")}
              priorLabel={priorLabel}
            />
          }
          breakdown={rollupBreakdown(qtdRollup)}
          history={histArr("qtd_closed")}
          baseline={planQuarter || undefined}
          onOpen={() => onOpenTile("closed_quarter")}
        />
        <BigKpiTile
          label={`Forecast`}
          value={fmtCents(qtdForecast)}
          sub={<WowSub delta={wow("qtd.forecast")} priorLabel={priorLabel} />}
          tone="positive"
          history={histArr("qtd_forecast")}
          onOpen={() => onOpenTile("forecast_quarter")}
        />
        <BigKpiTile
          label={`Upside`}
          value={fmtCents(qtdUpside)}
          sub={<WowSub delta={wow("qtd.upside")} priorLabel={priorLabel} />}
          tone="muted"
          history={histArr("qtd_upside")}
        />
      </div>
      <PlanCard
        label={quarterLabel}
        plan={planQuarter}
        forecast={qtdForecast}
        actual={qtdClosed}
      />

      {/* Live HubSpot Forecasting view — quarter */}
      <HubspotForecastCard
        fiscalYear={data.quarter_year}
        periodType="quarter"
        periodValue={quarter}
        periodLabel={quarterLabel}
      />

      <NewRevenueTable
        title={`New Revenue — ${quarterLabel}`}
        priorLabel={priorLabel}
        closed={data.closed_by_segment_quarter}
        forecast={data.forecast_by_segment_quarter}
        upside={data.upside_by_segment_quarter}
      />
      <ChurnContractionTable
        title={`Churn & Contraction — ${quarterLabel}`}
        priorLabel={priorLabel}
        closedCents={data.churn_quarter_cents}
        forecastCents={data.churn_forecast_quarter_cents}
      />
      <TotalRowCard
        label={`Total — ${quarterLabel}`}
        priorLabel={priorLabel}
        newRevenueClosed={qtdClosed}
        newRevenueForecast={qtdForecast}
        newRevenueUpside={qtdUpside}
        churnClosed={data.churn_quarter_cents}
        churnForecast={data.churn_forecast_quarter_cents}
      />

      {/* Cinch AI commentary — quarter-scope */}
      <LlmCommentaryCard
        commentary={data.llm_commentary}
        fiscalYear={data.quarter_year}
        periodType="quarter"
        periodValue={quarter}
        isoYear={isoYear}
        isoWeek={isoWeek}
      />

      {/* Pipeline by source */}
      <PipelineBySourceCard rows={data.pipeline_by_source} />

      {/* Executive summary stays at the bottom */}
      <NotesArea
        isoYear={isoYear}
        isoWeek={isoWeek}
        scope="exec_summary"
        initialBody={data.notes?.["exec_summary"] ?? ""}
        label="Executive summary"
        placeholder="Stan's executive summary for this week…"
        minHeightPx={120}
      />
    </>
  );
}

function PlanCard({
  label,
  plan,
  forecast,
  actual,
}: {
  label: string;
  plan: number;
  forecast: number;
  actual: number;
}) {
  const planPct = plan > 0 ? (actual / plan) * 100 : null;
  const forecastVsPlan = plan > 0 ? (forecast / plan) * 100 : null;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
          Plan vs. Forecast vs. Actual — {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        <div className="flex flex-wrap items-baseline gap-4 text-sm">
          <span className="font-mono">
            <span className="text-muted-foreground">Plan</span> {fmtCents(plan)}
          </span>
          <span className="font-mono">
            <span className="text-muted-foreground">Forecast</span>{" "}
            <span className="text-success">{fmtCents(forecast)}</span>
            {forecastVsPlan != null && (
              <span className="ml-1 text-muted-foreground">
                ({fmtPct(forecastVsPlan, 0)})
              </span>
            )}
          </span>
          <span className="font-mono">
            <span className="text-muted-foreground">Actual</span> {fmtCents(actual)}
            {planPct != null && (
              <span className="ml-1 text-muted-foreground">
                ({fmtPct(planPct, 0)})
              </span>
            )}
          </span>
        </div>
        {planPct != null && (
          <Progress value={Math.min(planPct, 100)} className="h-1.5" />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Tile sub-line with an optional week-over-week chip.
 *
 * The chip is omitted entirely when `delta` is null — which the BE returns at
 * the start of a new month, since a month-to-date number resets on the 1st and
 * comparing it to the previous month's final week would read as a collapse.
 */
function WowSub({
  label,
  delta,
  priorLabel,
}: {
  label?: string;
  delta: number | null;
  priorLabel: string;
}) {
  if (delta == null) return <>{label ?? null}</>;
  return (
    <span className="inline-flex items-center gap-1.5">
      {label && <span>{label}</span>}
      <DeltaCell value={delta} chip />
      <span className="text-[10px] text-muted-foreground">vs {priorLabel}</span>
    </span>
  );
}

/**
 * New / CS / Partner sub-numbers under the Closed headline.
 *
 *   New     — Direct NL (new logo)
 *   CS      — Upsell & Replacement; replacements counted at their like-for-like
 *             delta, so this goes negative on a contraction
 *   Partner — Direct Reseller + Upsell Reseller
 */
function rollupBreakdown(
  rollup: import("../api").ClosedRollup | undefined,
): KpiBreakdownItem[] | undefined {
  if (!rollup) return undefined;
  return [
    { label: "New", value: fmtCents(rollup.new_cents) },
    { label: "CS", value: fmtCents(rollup.cs_cents) },
    { label: "Partner", value: fmtCents(rollup.partner_cents) },
  ];
}

function sumSegment(
  rows: import("../api").SegmentClosed[],
  field: "closed_cents" | "forecast_cents" | "upside_cents",
): number {
  return rows.reduce(
    (acc, r) =>
      acc + ((r as unknown as Record<string, number | null>)[field] ?? 0),
    0,
  );
}
